/**
 * PrimaryActionButton - Main action button for modules
 * Used for: Generate, Upscale, Expand, Remove, Inpaint - the main action in each module
 * 
 * Uses CSS class .primary-action-btn for the soft-neutral default state and accent hover
 * 
 * Content is always single-line (nowrap). If you need an icon, pass it as a child
 * alongside text — the flexbox layout keeps them inline.
 * 
 * When loading=true, a built-in spinner replaces the children automatically.
 * 
 * @param {ReactNode} children - Button label/content
 * @param {function} onClick - Click handler
 * @param {boolean} disabled - Whether button is disabled (no image loaded, etc.)
 * @param {boolean} loading - Whether action is in progress (shows spinner)
 * @param {string} loadingText - Text to show when loading (defaults to children)
 * @param {boolean} allowClickWhileLoading - If true, button stays clickable during loading (for queueing multiple actions)
 * @param {string} size - "default" | "compact" — compact uses smaller font/padding for toolbar use
 * @param {string} variant - "default" | "destructive" | "brand"
 *   destructive uses a red background for dangerous actions.
 *   brand is the special-occasion treatment: the face goes full white on hover instead
 *   of the accent, with the shared brand bloom burning behind it. It is deliberately
 *   scarce — one per surface at most, for the action that should feel like an event
 *   (the projects rail's Create Project). Do not reach for it to make an ordinary
 *   action louder.
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
import BaseButton from './base/BaseButton';

const LoadingSpinner = () => (
  <svg
    className="animate-spin"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

const PrimaryActionButton = ({ 
  children, 
  onClick, 
  disabled = false,
  loading = false,
  loadingText,
  allowClickWhileLoading = false,
  size = 'default',
  variant = 'default',
  className = '',
  style = {},
  type = 'button',
  ...buttonProps
}) => {
  const isDisabled = disabled || (loading && !allowClickWhileLoading);
  const loadingContent = loadingText === undefined ? children : loadingText;

  const classes = [
    'primary-action-btn',
    size === 'compact' && 'btn-compact',
    size === 'small' && 'btn-small',
    variant === 'destructive' && 'btn-destructive-primary',
    // The bloom rides with the variant rather than with the caller, so a brand button
    // cannot be half-applied.
    variant === 'brand' && 'btn-brand-primary brand-bloom-hover',
    className
  ].filter(Boolean).join(' ');

  return (
    <BaseButton
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={classes}
      style={style}
      {...buttonProps}
    >
      <span className="primary-action-btn-content">
        {loading ? (
          <>
            <LoadingSpinner />
            {loadingContent}
          </>
        ) : children}
      </span>
    </BaseButton>
  );
};

export default PrimaryActionButton;
